import { useState } from 'react';
import type { ReactNode } from 'react';
import type { Language } from '../i18n/types';
import { translations } from '../i18n/translations';
import { LanguageContext } from './LanguageContextDefinition';
import type { LanguageContextValue } from './LanguageContextDefinition';

/**
 * 语言提供者组件的 Props
 */
interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * 从嵌套对象中根据路径获取值
 * @param obj - 目标对象
 * @param path - 点分隔的路径字符串，如 'hero.title'
 * @returns 对应路径的值，如果路径无效则返回 key 本身
 */
function getNestedValue(obj: Record<string, unknown>, path: string): string {
  const keys = path.split('.');
  let current: unknown = obj;

  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = (current as Record<string, unknown>)[key];
    } else {
      // 路径无效，返回原始 key
      return path;
    }
  }

  // 如果最终值是字符串，返回它；否则返回原始 key
  return typeof current === 'string' ? current : path;
}

/**
 * 检测浏览器默认语言
 * @returns 'zh' 或 'en'
 */
function detectBrowserLanguage(): Language {
  const browserLang = navigator.language || (navigator as { userLanguage?: string }).userLanguage;
  return browserLang?.startsWith('zh') ? 'zh' : 'en';
}

/**
 * 从 localStorage 读取语言偏好
 * @returns 保存的语言或 null
 */
function loadLanguageFromStorage(): Language | null {
  try {
    const saved = localStorage.getItem('language');
    if (saved === 'zh' || saved === 'en') {
      return saved;
    }
    return null;
  } catch (error) {
    console.warn('Failed to load language from localStorage:', error);
    return null;
  }
}

/**
 * 保存语言偏好到 localStorage
 * @param lang - 要保存的语言
 */
function saveLanguageToStorage(lang: Language): void {
  try {
    localStorage.setItem('language', lang);
  } catch (error) {
    console.warn('Failed to save language to localStorage:', error);
  }
}

/**
 * 语言提供者组件
 * 
 * 提供语言状态管理和翻译功能：
 * - 从 localStorage 读取用户语言偏好
 * - 默认根据浏览器语言判断
 * - 支持切换语言并保存到 localStorage
 * - 提供 t() 翻译函数
 */
export function LanguageProvider({ children }: LanguageProviderProps) {
  // 初始化语言状态：优先使用 localStorage，其次使用浏览器语言，默认中文
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLang = loadLanguageFromStorage();
    if (savedLang) {
      return savedLang;
    }
    return detectBrowserLanguage();
  });

  /**
   * 设置语言并保存到 localStorage
   * @param lang - 目标语言
   */
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    saveLanguageToStorage(lang);
  };

  /**
   * 翻译函数
   * @param key - 翻译键，使用点分隔的路径，如 'hero.title'
   * @returns 对应语言的翻译文本
   */
  const t = (key: string): string => {
    const currentTranslations = translations[language];
    return getNestedValue(currentTranslations as unknown as Record<string, unknown>, key);
  };

  const value: LanguageContextValue = {
    language,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}
